import React from 'react';
import { 
    Box, 
    IconButton, 
    Typography, 
    Select, 
    MenuItem, 
    SelectChangeEvent,
    useTheme
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useTranslation } from '../../hooks/useTranslation';

interface StyledPaginationProps {
    count: number;
    page: number;
    rowsPerPage: number;
    rowsPerPageOptions: number[];
    onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
    onRowsPerPageChange: (event: SelectChangeEvent<HTMLInputElement>) => void;
    labelRowsPerPage?: string;
    labelDisplayedRows?: (props: { from: number; to: number; count: number }) => string;
}

const StyledPagination: React.FC<StyledPaginationProps> = ({
    count,
    page,
    rowsPerPage,
    rowsPerPageOptions,
    onPageChange,
    onRowsPerPageChange,
    labelRowsPerPage: labelRowsPerPageProp,
    labelDisplayedRows: labelDisplayedRowsProp,
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const labelRowsPerPage = labelRowsPerPageProp || t('common.labelRowsPerPage');

    const labelDisplayedRows: (props: { from: number; to: number; count: number }) => string = ({ from, to, count }) => {
        if (labelDisplayedRowsProp) {
            return labelDisplayedRowsProp({ from, to, count });
        }
        return `${from}-${to} ${t('common.table.pagination.of')} ${count}`;
    };
    
    const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page + 1);
    };

    const from = count === 0 ? 0 : page * rowsPerPage + 1;
    const to = Math.min(count, (page + 1) * rowsPerPage);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                py: 2,
                px: 1,
                borderTop: '1px solid',
                borderTopColor: theme.palette.divider,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                <Typography variant="body2" sx={{ mr: 1 }}>
                    {labelRowsPerPage}
                </Typography>
                <Select
                    value={rowsPerPage}
                    onChange={onRowsPerPageChange}
                    size="small"
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                bgcolor: (theme) => theme.palette.background.paper,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: (theme) => theme.palette.divider,
                                boxShadow: (theme) => `0px 5px 15px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                            }
                        }
                    }}
                    sx={{
                        minWidth: 80,
                        '.MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.divider,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                        },
                    }}
                >
                    {rowsPerPageOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            </Box>
            
            <Typography variant="body2" sx={{ mr: 2 }}>
                {labelDisplayedRows({ from, to, count })}
            </Typography>
            
            <Box
                sx={{
                    display: 'flex',
                    overflow: 'hidden',
                }}
            >
                <IconButton
                    onClick={handleBackButtonClick}
                    disabled={page === 0}
                    size="small"
                    sx={{
                        minWidth: '40px',
                        p: 1,
                        bgcolor: 'transparent',
                        '&.Mui-disabled': {
                            opacity: 0.5,
                        },
                        '&:hover': {
                            backgroundColor: 'rgba(255, 73, 0, 0.1)',
                        },
                    }}
                >
                    <NavigateBeforeIcon />
                </IconButton>
                <IconButton
                    onClick={handleNextButtonClick}
                    disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                    size="small"
                    sx={{
                        minWidth: '40px',
                        p: 1,
                        ml: 1,
                        bgcolor: 'transparent',
                        '&.Mui-disabled': {
                            opacity: 0.5,
                        },
                        '&:hover': {
                            backgroundColor: 'rgba(255, 73, 0, 0.1)',
                        },
                    }}
                >
                    <NavigateNextIcon />
                </IconButton>
            </Box>
        </Box>
    );
};

export default StyledPagination;
